import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { formatDate, formatMass, formatNumber, titleCase } from '../../lib/format'
import type { ListResponse, ProductionLog } from '../../types'
import {
  Alert, Button, Card, CardBody, CardHeader, EmptyState, Select, Skeleton, StatusBadge,
} from '../../components/ui'
import DownloadButton from '../../components/DownloadButton'
import { ChartIcon } from '../../components/icons'
import { RangeChips, WeeklyBarChart, bucketWeekly } from '../../components/charts'
import '../miner/dashboard.css'

type GovProductionLog = ProductionLog & { org_id?: string; reporter_uid?: string }

const MINERALS = [
  'gold', 'copper', 'cobalt', 'manganese', 'emerald', 'amethyst',
  'aquamarine', 'tourmaline', 'limestone', 'granite', 'coal', 'other',
]

export default function GovProduction() {
  const [mineral, setMineral] = useState('')
  // Summary = totals + trend; Table = the raw log ledger. Same
  // Summary|Table split as the rest of the reporting pages.
  const [view, setView] = useState<'summary' | 'table'>('summary')
  const [weeks, setWeeks] = useState(8)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['gov', 'production', mineral],
    queryFn: () => api.get<ListResponse<GovProductionLog>>(
      `/gov/production${mineral ? `?mineral=${encodeURIComponent(mineral)}` : ''}`,
    ),
  })

  const items = useMemo(() => data?.items ?? [], [data])

  // Aggregate by mineral so the regulator sees output, not just rows.
  const byMineral = useMemo(() => {
    const totals = new Map<string, number>()
    for (const p of items) {
      const n = typeof p.volume_kg === 'number' ? p.volume_kg : Number(p.volume_kg)
      if (Number.isNaN(n)) continue
      const key = p.mineral_type || 'unknown'
      totals.set(key, (totals.get(key) ?? 0) + n)
    }
    return [...totals.entries()].sort((a, b) => b[1] - a[1])
  }, [items])

  const totalKg = byMineral.reduce((s, [, kg]) => s + kg, 0)

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Production oversight</h1>
          <p className="page-head__sub">
            Declared output across cooperatives in your area. Read-only.
          </p>
        </div>
        <div className="row" style={{ alignItems: 'center' }}>
          <Select aria-label="Filter by mineral" value={mineral}
                  onChange={(e) => setMineral(e.target.value)}>
            <option value="">All minerals</option>
            {MINERALS.map((m) => <option key={m} value={m}>{titleCase(m)}</option>)}
          </Select>
          <DownloadButton path="/api/v1/gov/exports/production.xlsx" filename="production_report.xlsx">
            Export Excel
          </DownloadButton>
          <DownloadButton path="/api/v1/gov/exports/production.pdf" filename="production_report.pdf">
            Export PDF
          </DownloadButton>
        </div>
      </div>

      {isError && <Alert tone="danger">{(error as Error).message}</Alert>}

      <div className="tab-scroll" style={{ marginBottom: 'var(--sp-5)' }}>
        <Button variant={view === 'summary' ? 'primary' : 'secondary'} size="sm" onClick={() => setView('summary')}>
          Summary
        </Button>
        <Button variant={view === 'table' ? 'primary' : 'secondary'} size="sm" onClick={() => setView('table')}>
          Table
        </Button>
      </div>

      {view === 'summary' && (<>
      <div className="grid grid--stats" style={{ marginBottom: 'var(--sp-6)' }}>
        <Card>
          <div className="stat">
            <div className="stat__top">
              <span className="stat__label">Total declared</span>
              <span className="stat__icon"><ChartIcon /></span>
            </div>
            {isLoading ? <Skeleton h={34} w="60%" />
                       : <div className="stat__value">{formatMass(totalKg)}</div>}
            <div className="stat__meta">{items.length} log{items.length === 1 ? '' : 's'}</div>
          </div>
        </Card>
        {byMineral.slice(0, 3).map(([m, kg]) => (
          <Card key={m}>
            <div className="stat">
              <div className="stat__top">
                <span className="stat__label">{titleCase(m)}</span>
              </div>
              <div className="stat__value">{formatMass(kg)}</div>
              <div className="stat__meta">
                {totalKg ? `${Math.round((kg / totalKg) * 100)}% of declared output` : '—'}
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader title="Declared output per week"
                    sub={`Volume declared${mineral ? ` (${titleCase(mineral)})` : ''}, last ${weeks} weeks`} />
        <CardBody>
          <RangeChips value={weeks} onChange={setWeeks} />
          {isLoading ? (
            <Skeleton h={140} />
          ) : (() => {
            const s = bucketWeekly(items, (p) => p.period_start || p.created_at,
              (p) => Number(p.volume_kg) || 0, weeks)
            return <WeeklyBarChart weeks={s.weeks} values={s.values} unit=" kg" />
          })()}
        </CardBody>
      </Card>
      </>)}

      {view === 'table' && (
      <Card>
        <CardHeader title="Production logs"
                    sub={items.length ? `${items.length} record${items.length === 1 ? '' : 's'}` : undefined} />
        <CardBody flush>
          {isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }} className="stack">
              <Skeleton h={20} /><Skeleton h={20} w="74%" /><Skeleton h={20} w="60%" />
            </div>
          ) : items.length === 0 ? (
            <EmptyState icon={<ChartIcon size={22} />} title="No production declared"
                        body="Logs submitted by cooperatives in your area will appear here." />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr><th>Cooperative</th><th>Mineral</th><th className="table__num">Volume</th>
                      <th>Period</th><th>Buyer</th><th className="table__num">Value</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {items.map((p, i) => (
                    <tr key={p.id ?? i}>
                      <td>{p.org_id || '—'}</td>
                      <td>{titleCase(p.mineral_type)}</td>
                      <td className="table__num">{formatMass(p.volume_kg)}</td>
                      <td>{formatDate(p.period_start)}</td>
                      <td>{p.buyer_name || '—'}</td>
                      <td className="table__num">{formatNumber(p.value_local, 2)}</td>
                      <td><StatusBadge status={p.status || 'submitted'} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
      )}
    </>
  )
}
